class TSMainProduct extends HTMLElement {
  constructor() {
    super();
    this.isLoading = false;
  }

  connectedCallback() {
    this.productVariants = JSON.parse(this.querySelector("[data-dynamic-variants]").textContent);
    this.cart = document.querySelector("ts-cart-drawer");
    this.gallery = this.querySelector('[id^="ProductGallery-"] ts-slider-component');
    this.navGallery = this.querySelector('[id^="ProductGalleryNav-"]');

    this.querySelector('[id^="ProductOptions-"]')?.addEventListener("change", this.onVariantChange.bind(this));
    this.querySelector('[id^="ProductForm-"]')?.addEventListener("submit", this.onFormSubmit.bind(this));
    this.initClickListeners();

    /* Back from checkout restores the page from the bfcache as it was left: Hormona+ popup open,
       its buttons disabled and busy. Reset it so the page is usable again. */
    window.addEventListener("pageshow", (event) => {
      if (event.persisted) this.resetAddonModal();
    });
  }

  resetAddonModal() {
    this.toggleLoading(true);

    const modal = this.querySelector("[data-hormona-plus-modal]");
    if (!modal) return;

    modal.querySelectorAll("[data-addon-choice]").forEach(button => {
      button.disabled = false;
      button.removeAttribute("aria-busy");
    });
    if (modal.open) modal.close();
  }

  onVariantChange(event) {
    const searchParams = window.location.search
      ? new URLSearchParams(window.location.search)
      : new URLSearchParams();

    this.querySelector("input[name='id']").value = event.target.value;
    searchParams.set('variant', event.target.value);

    const newProductUrl = window.location.origin + window.location.pathname + `?${searchParams.toString()}`;
    window.history.replaceState({}, '', newProductUrl);
    window.dispatchEvent(new CustomEvent("variant-change", { detail: { variantId: event.target.value } }));

    this.querySelector('[id^="ProductSubmit-"]').toggleAttribute("disabled", event.target.dataset.available !== "true");
  }

  onFormSubmit(event = false) {
    if (event) event.preventDefault();

    if (this.isLoading) return;

    const sections = [];
    if (this.cart) sections.push("ts-cart-drawer");

    const cartData = {
      items: [
        {
          id: parseInt(this.querySelector("[name='id']").value),
          quantity: parseInt(this.querySelector("[name='quantity']").value)
        }
      ],
      sections: sections,
      sections_url: window.location.pathname
    };

    /* v261001 (HT-4855): ask about Hormona+ first; the choice decides whether it is added */
    const addonModal = this.querySelector("[data-hormona-plus-modal]");
    if (addonModal) return this.openAddonModal(addonModal, cartData);

    this.addToCart(cartData);
  }

  openAddonModal(modal, cartData) {
    this.pendingCartData = cartData;

    if (!modal.dataset.bound) {
      modal.dataset.bound = "true";
      modal.addEventListener("click", (e) => {
        const choice = e.target.closest("[data-addon-choice]");
        if (choice) return this.chooseAddon(modal, choice);
        if (this.isLoading) return;
        if (e.target === modal) modal.close(); /* backdrop: dismiss without ordering */
      });
      modal.addEventListener("cancel", (e) => {
        if (this.isLoading) e.preventDefault();
      });
    }

    modal.showModal();
    this.fillAddonDetails(modal);
  }

  /* The popup image and the struck-through regular price are rendered by Liquid when it found
     the product; otherwise they are filled from the /products.json lookup and stay hidden if
     that finds nothing. */
  fillAddonDetails(modal) {
    const compareEl = modal.querySelector("[data-addon-price-compare]");
    const media = modal.querySelector("[data-addon-image]");
    const needsImage = media && !media.querySelector("img");
    const needsPrice = compareEl && compareEl.hidden;
    if (!needsImage && !needsPrice) return;

    this.resolveAddonVariant().then(() => {
      const variant = this.addonVariant;
      if (!variant) return;

      const imageSrc = variant.featured_image?.src || this.addonProduct?.images?.[0]?.src;
      if (needsImage && imageSrc) {
        const img = document.createElement("img");
        img.src = imageSrc + (imageSrc.includes("?") ? "&" : "?") + "width=900";
        img.alt = this.addonProduct?.title || "";
        media.appendChild(img);
      }
      if (!needsPrice) return;

      const toCents = value => Math.round(parseFloat(value) * 100);
      const price = toCents(variant.price);
      const compare = variant.compare_at_price ? toCents(variant.compare_at_price) : 0;
      const format = (modal.dataset.moneyFormat || "{{amount}}").replace(/<[^>]*>/g, "");

      compareEl.textContent = this.formatMoney(Math.max(price, compare), format);
      compareEl.hidden = false;
    });
  }

  formatMoney(cents, format) {
    const amount = (cents / 100).toFixed(2);
    const withCommas = (value, thousands, decimal) => {
      const [whole, fraction] = value.split(".");
      return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (fraction ? decimal + fraction : "");
    };

    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
      switch (key) {
        case "amount_no_decimals": return withCommas(Math.round(cents / 100).toString(), ",", ".");
        case "amount_with_comma_separator": return withCommas(amount, ".", ",");
        case "amount_no_decimals_with_comma_separator": return withCommas(Math.round(cents / 100).toString(), ".", ",");
        default: return withCommas(amount, ",", ".");
      }
    });
  }

  /* Both the main button ("add") and the close button ("skip") go to checkout; only "add"
     sends the Hormona+ product and its discount code. On an error the modal closes so the
     message under the hero button can be seen. */
  chooseAddon(modal, choice) {
    if (this.isLoading) return;

    const buttons = modal.querySelectorAll("[data-addon-choice]");
    buttons.forEach(button => button.disabled = true);
    choice.setAttribute("aria-busy", "true");

    this.addToCart(this.pendingCartData, choice.dataset.addonChoice === "add").then(ok => {
      if (ok) return;
      buttons.forEach(button => button.disabled = false);
      choice.removeAttribute("aria-busy");
      modal.close();
    });
  }

  addToCart(data, withAddon = false) {
    if (!data) return Promise.resolve(false);

    this.toggleLoading();

    return fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    })
    .then(response => response.json())
    .then(response => {
      if (!response.items) {
        const error = new Error(response.description);
        throw error;
      } else {
        return (withAddon ? this.addAddon() : Promise.resolve()).then(() => {
          this.toggleLoading();

          /* /checkout?discount= rather than /discount/CODE - shop.hormona.io sends /discount to www */
          const code = withAddon ? this.querySelector("[data-hormona-plus-modal]")?.dataset.discountCode : "";
          window.location.href = window.Shopify.routes.root + "checkout" + (code ? "?discount=" + encodeURIComponent(code) : "");
          return true;
        });
      }
    })
    .catch(error => {
      this.showError(error);
      return false;
    })
    .finally(() => this.toggleLoading(true));
  }

  /* Optional extra item (the Hormona+ product on v261001), sent after the kit as its own
     request so a missing or unavailable product never blocks the kit or shows an error -
     the reason only goes to the console. Skipped when it is already in the cart, so
     ordering again doesn't add a second one. */
  addAddon() {
    return this.resolveAddonVariant()
      .then(addonId => {
        if (!addonId) return;

        return fetch(window.Shopify.routes.root + 'cart.js')
          .then(response => response.json())
          .then(cart => {
            if (cart.items.some(line => line.variant_id === addonId)) return;

            return fetch(window.Shopify.routes.root + 'cart/add.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ items: [{ id: addonId, quantity: 1 }] }),
            })
            .then(response => response.json())
            .then(response => {
              if (!response.items) console.warn("Hormona+ add-on not added:", response.description || response.message);
            });
          });
      })
      .catch(error => console.warn("Hormona+ add-on not added:", error));
  }

  /* Variant id from the page; when Liquid could not find the product (it only scans the
     first 50 in collections.all), look it up by product id in /products.json instead. */
  resolveAddonVariant() {
    const variantId = parseInt(this.querySelector("[data-addon-variant-id]")?.value);
    if (variantId) return Promise.resolve(variantId);

    const fallback = this.querySelector("[data-addon-product-id]");
    const productId = parseInt(fallback?.dataset.addonProductId);
    if (!productId) return Promise.resolve(null);

    const findOnPage = (page) =>
      fetch(window.Shopify.routes.root + `products.json?limit=250&page=${page}`)
        .then(response => response.json())
        .then(data => {
          const products = data.products || [];
          const product = products.find(p => p.id === productId);
          if (product) {
            const variant = product.variants.find(v => v.available);
            this.addonProduct = product;
            this.addonVariant = variant || null;
            if (!variant) console.warn("Hormona+ add-on skipped: product " + productId + " has no available variant");
            return variant ? variant.id : null;
          }
          if (products.length === 250 && page < 4) return findOnPage(page + 1);

          console.warn("Hormona+ add-on skipped: product " + productId + " not found (" + fallback.dataset.addonSkipped + ", and not in /products.json - is it on the Online Store?)");
          return null;
        });

    return findOnPage(1);
  }

  initClickListeners() {
    this.addEventListener("click", (e) => {
      if (e.target.hasAttribute("data-action")) {
        switch (e.target.dataset.action) {
          case "change-slide":
            this.gallery.scrollToSlide(parseInt(e.target.dataset.slide));
            break;
          case "fact-open":
            const fact = this.querySelector(`[data-fact-popup="${e.target.dataset.fact}"]`);
            fact?.classList.add("open");
          case "fact-close":
            e.target.closest("[data-fact-popup]").classList.remove("open");
            break;
        }
      }
    })

    this.addEventListener("slider-updated", (e) => {
      this.navGallery.querySelectorAll("li").forEach(slide => {
        slide.classList.toggle("active", slide.dataset.slide == e.detail.currentSlide)
      })
    })
  }

  showError(error) {
    console.error(error);

    if (!this.querySelector("[product-notification]")) return;

    const messageBlock = this.querySelector("[product-notification]");

    messageBlock.innerText = error.message;
    setTimeout(() => {messageBlock.innerText = ''}, 5000);
    messageBlock.scrollIntoView({behavior: "smooth",block:"center"});
  }

  toggleLoading(turnOff = false) {
    this.isLoading = !this.isLoading;
    if (turnOff) this.isLoading = false;
    this.classList.toggle("loading", this.isLoading);
  }
}

if (document.querySelector("ts-main-product")) {
  customElements.define("ts-main-product", TSMainProduct);
}

class TSSliderComponent extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.currentSlide = 1;
    this.allowEvents = this.getAttribute("data-allow-events") === "true";
    this.hideControls = this.getAttribute("data-hide-controls") !== "false"

    this.slider = this.querySelector('[id^="Slider-"]');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    this.scrollbar = this.querySelector("[slider-scrollbar]");
    this.scrollbarThumb = this.querySelector("[slider-scrollbar] span");

    this.pagination = this.querySelector('[slider-pagination]');

    if (!this.slider) return;

    this.update();
    window.addEventListener('resize', this.update.bind(this));
    this.slider.addEventListener('scroll', this.update.bind(this));

    if (this.prevButton && this.nextButton) {
      this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
      this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
    }

    if (this.pagination) {
      this.pagination.addEventListener("click", this.onDotClick.bind(this))
    }
  }

  onButtonClick(event) {
    event.preventDefault();
    if (event.currentTarget.name === 'previous' && this.currentSlide > 1) {
      this.currentSlide -= 1;
    } else if (event.currentTarget.name === 'next' && this.currentSlide < this.maxLength) {
      this.currentSlide += 1;
    }
    this.scrollToSlide(this.currentSlide);
  }

  onDotClick(event) {
    event.preventDefault();
    if (!event.target.hasAttribute("data-slide")) return;

    const slideIndex = parseInt(event.target.dataset.slide);
    this.scrollToSlide(slideIndex);
  }

  scrollToSlide(index) {
    this.slider.scrollTo({
      left: this.querySelector(`[id^="Slide-"]:nth-child(${index})`).offsetLeft,
      behavior: 'smooth'
    });
  }

  update() {
    const items = this.querySelectorAll('[id^="Slide-"]');
    const scrollLeft = this.slider.scrollLeft;
    const scrollWidth = this.slider.scrollWidth;
    const clientWidth = this.slider.clientWidth;
    const blockWidth = items[0]?.offsetWidth ?? 0;
    const gap = window.innerWidth < 750 ? 16 : 24;
    const hasScroll = this.slider.scrollWidth > clientWidth;
      
    this.maxLength = items.length - (Math.floor(clientWidth / blockWidth) - 1);
    this.currentSlide = Math.round(scrollLeft / (blockWidth + gap)) + 1;
    
    if (this.prevButton && this.nextButton) {
      this.prevButton.toggleAttribute("disabled", this.currentSlide == 1);
      this.nextButton.toggleAttribute("disabled", this.currentSlide == this.maxLength);
  
      if (this.hideControls) {
        this.prevButton.classList.toggle("hidden", !hasScroll);
        this.nextButton.classList.toggle("hidden", !hasScroll);
      }
    }

    if (this.scrollbarThumb) {      
      const thumbWidth = (clientWidth / scrollWidth) * 100;
      const thumbPosition = (scrollLeft / (scrollWidth - clientWidth)) * 100;

      this.scrollbarThumb.style.left = `${thumbPosition}%`;
      this.scrollbarThumb.style.width = `${thumbWidth}%`;
      this.scrollbarThumb.style.transform = `translateX(-${thumbPosition}%)`;
      this.scrollbar.classList.toggle("hidden", !hasScroll);
    }

    if (this.pagination) {
      this.updatePagination();
    }
  
    if (this.allowEvents) {
      this.dispatchEvent(
        new CustomEvent("slider-updated", {
          detail: {
            currentSlide: this.currentSlide
          },
          bubbles: true,
        })
      );
    }
  }

  updatePagination() {
    const slides = this.querySelectorAll('[id^="Slide-"]');

    if (!this.slider || slides.length === 0 || !this.pagination) return;

    const containerWidth = this.slider.clientWidth;
    const slideWidth = slides[0].offsetWidth;

    const slidesPerPage = Math.floor(containerWidth / slideWidth);
    const totalPages = slides.length - (slidesPerPage - 1);

    this.pagination.innerHTML = '';
    this.pagination.classList.toggle("hidden", totalPages === 1);

    for (let i = 1; i <= totalPages; i++) {
      const dot = document.createElement('span');
      dot.setAttribute("data-slide", i);
      if (i === this.currentSlide) dot.classList.add("active");
      this.pagination.appendChild(dot);
    }
  }
}

customElements.define("ts-slider-component", TSSliderComponent);